<script setup>

import {computed} from "vue";
import {useRoute} from "vue-router";
import {usePortalContentAndDesignStore} from "../../../stores/partners/portalContentAndDesignStore";
import EditableTextField from "../portal-editable-fields/EditableTextField.vue";

const route = useRoute();
const portalContentAndDesignStore = usePortalContentAndDesignStore();

const isLeadsPage = computed(() => route.name === 'leads');

const noDataNotice = computed({
    get() {
        if (isLeadsPage.value) {
            return portalContentAndDesignStore.translationSettings.portal_translation_leads_no_data_notice
                || 'You haven\'t added any leads yet — submit your first one to see it listed here!';
        }
        return portalContentAndDesignStore.translationSettings.portal_translation_features_ref_lives_here_notice
            || 'Your referrals will appear here once they start coming in.';
    },
    set(value) {
        if (isLeadsPage.value) {
            portalContentAndDesignStore.translationSettings.portal_translation_leads_no_data_notice = value;
        } else {
            portalContentAndDesignStore.translationSettings.portal_translation_features_ref_lives_here_notice = value;
        }
    }
});

</script>

<template>
    <div class="relative">
        <div class="flex absolute inset-0 justify-center items-center font-medium bg-white bg-opacity-50 z-10">
            <div class="p-4 bg-white rounded opacity-75">
                <div class="flex justify-center items-center pb-4"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path fill="none" d="M0 0h24v24H0z"></path><path d="M17.841 15.659l.176.177.178-.177a2.25 2.25 0 0 1 3.182 3.182l-3.36 3.359-3.358-3.359a2.25 2.25 0 0 1 3.182-3.182zM12 14v2a6 6 0 0 0-6 6H4a8 8 0 0 1 7.75-7.996L12 14zm0-13c3.315 0 6 2.685 6 6a5.998 5.998 0 0 1-5.775 5.996L12 13c-3.315 0-6-2.685-6-6a5.998 5.998 0 0 1 5.775-5.996L12 1zm0 2C9.79 3 8 4.79 8 7s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"></path></svg></div>
                <editable-text-field
                    text-type="p"
                    custom-classes="text-sm tracking-wide opacity-60 text-center"
                    v-model="noDataNotice"
                />
            </div>
        </div>
        <table class="min-w-full divide-y divide-gray-200 divide-opacity-75">
            <thead>
                <tr>
                    <td colspan="4" class="p-4 text-sm font-medium text-center text-opacity-100">
                        <div class="mx-auto w-4/12 h-3"></div>
                    </td>
                </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200 divide-opacity-75">
                <tr>
                    <th scope="col" class="py-4 pr-4 pl-8 opacity-60">
                        <div class="w-full h-3 bg-gray-200 rounded-lg"></div>
                    </th>
                    <th scope="col" class="px-4 py-4 opacity-60">
                        <div class="w-full h-3 bg-gray-200 rounded-lg"></div>
                    </th>
                    <th scope="col" class="px-4 py-4 opacity-60">
                        <div class="w-full h-3 bg-gray-200 rounded-lg"></div>
                    </th>
                    <th scope="col" class="py-4 pr-8 pl-4 opacity-60">
                        <div class="w-full h-3 bg-gray-200 rounded-lg"></div>
                    </th>
                </tr>
                <tr class="hover:bg-gray-50">
                    <td class="py-4 pr-4 pl-8">
                        <div class="h-3 bg-gray-200 w-6/12 rounded-lg mb-1.5"></div>
                        <div class="w-10/12 h-3 bg-gray-200 bg-opacity-70 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-4 bg-indigo-100 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                    <td class="py-4 pr-8 pl-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                </tr>
                <tr class="hover:bg-gray-50">
                    <td class="py-4 pr-4 pl-8">
                        <div class="h-3 bg-gray-200 w-6/12 rounded-lg mb-1.5"></div>
                        <div class="w-10/12 h-3 bg-gray-200 bg-opacity-70 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-4 bg-indigo-100 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                    <td class="py-4 pr-8 pl-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                </tr>
                <tr class="hover:bg-gray-50">
                    <td class="py-4 pr-4 pl-8">
                        <div class="h-3 bg-gray-200 w-6/12 rounded-lg mb-1.5"></div>
                        <div class="w-10/12 h-3 bg-gray-200 bg-opacity-70 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-4 bg-indigo-100 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                    <td class="py-4 pr-8 pl-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                </tr>
                <tr class="hover:bg-gray-50">
                    <td class="py-4 pr-4 pl-8">
                        <div class="h-3 bg-gray-200 w-6/12 rounded-lg mb-1.5"></div>
                        <div class="w-10/12 h-3 bg-gray-200 bg-opacity-70 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-4 bg-indigo-100 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                    <td class="py-4 pr-8 pl-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                </tr>
                <tr>
                    <td colspan="4" class="p-4 text-sm font-medium text-center text-opacity-100">
                        <div class="mx-auto w-4/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
